/**
 * Datos de un producto: codigo, nombre, precio, categoria y variante (ADM-02).
 *
 * Es el mismo formulario para crear y para editar, porque son los mismos
 * campos. La unica diferencia es el stock inicial, que solo existe en el alta:
 * despues, el stock cambia unicamente con un movimiento de inventario, que es lo
 * que deja el rastro (`PanelStockProducto`).
 *
 * Las variantes se arman escribiendo el MISMO grupo en cada talla o color. Es un
 * campo de texto con sugerencias y no una entidad aparte: son pocos productos, y
 * asi crear una talla nueva es copiar el grupo de su hermana y nada mas.
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import type { CategoriaAdminResumen, DatosProductoDto, UnidadMedida } from '@bandu/compartido';

import { Icono } from '../comunes/Icono';
import { EntradaMonto } from './EntradaMonto';

export interface ValoresProducto extends DatosProductoDto {
  stockInicial: number;
}

export interface FormularioProductoProps {
  inicial: ValoresProducto;
  categorias: CategoriaAdminResumen[];
  /** Grupos de variantes que ya existen, para sugerirlos al escribir. */
  gruposExistentes?: string[];
  /** En el alta se pide el stock inicial; en la edicion no. */
  conStockInicial: boolean;
  textoBoton: string;
  enviando: boolean;
  falla: string | null;
  guardado?: boolean;
  onGuardar(valores: ValoresProducto): void;
}

/** Texto de un campo numerico a entero, o `null` si quedo vacio o no es un numero. */
function enteroDe(texto: string): number | null {
  const limpio = texto.replace(/[.\s$]/g, '');
  if (limpio.length === 0) return null;
  const valor = Number(limpio);
  return Number.isInteger(valor) && valor >= 0 ? valor : null;
}

function textoONulo(texto: string): string | null {
  const limpio = texto.trim();
  return limpio.length > 0 ? limpio : null;
}

export function FormularioProducto({
  inicial,
  categorias,
  gruposExistentes = [],
  conStockInicial,
  textoBoton,
  enviando,
  falla,
  guardado = false,
  onGuardar,
}: FormularioProductoProps) {
  const id = useId();
  const [sku, setSku] = useState(inicial.sku);
  const [nombre, setNombre] = useState(inicial.nombre);
  const [marca, setMarca] = useState(inicial.marca ?? '');
  const [categoriaId, setCategoriaId] = useState(inicial.categoriaId);
  const [precio, setPrecio] = useState(String(inicial.precio));
  const [precioAnterior, setPrecioAnterior] = useState(
    inicial.precioAnterior !== null ? String(inicial.precioAnterior) : '',
  );
  const [unidadMedida, setUnidadMedida] = useState<UnidadMedida>(inicial.unidadMedida);
  const [grupo, setGrupo] = useState(inicial.grupoVariante ?? '');
  const [etiqueta, setEtiqueta] = useState(inicial.etiquetaVariante ?? '');
  const [orden, setOrden] = useState(String(inicial.ordenVariante));
  const [stockInicial, setStockInicial] = useState(String(inicial.stockInicial));
  const [aviso, setAviso] = useState<string | null>(null);

  const conGrupo = grupo.trim().length > 0;

  /** Lo que el navegador no valida solo. Devuelve el mensaje, o `null` si esta todo bien. */
  function problemaDe(precioNumero: number | null, anteriorNumero: number | null): string | null {
    if (precioNumero === null) return 'El precio tiene que ser un número, sin decimales.';
    if (precioAnterior.trim().length > 0 && anteriorNumero === null) {
      return 'El precio anterior tiene que ser un número, o quedar vacío.';
    }
    if (conGrupo && etiqueta.trim().length === 0) {
      return 'Si el producto es parte de un grupo, escribe qué opción es (talla, color…).';
    }
    if (sku.trim().length === 0 || nombre.trim().length < 2) {
      return 'El nombre y el código son obligatorios.';
    }
    return null;
  }

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();

    const precioNumero = enteroDe(precio);
    const anteriorNumero = precioAnterior.trim().length > 0 ? enteroDe(precioAnterior) : null;

    const problema = problemaDe(precioNumero, anteriorNumero);
    if (problema !== null || precioNumero === null) {
      setAviso(problema);
      return;
    }

    setAviso(null);
    onGuardar({
      sku: sku.trim(),
      nombre: nombre.trim(),
      marca: textoONulo(marca),
      categoriaId,
      precio: precioNumero,
      precioAnterior: anteriorNumero,
      unidadMedida,
      grupoVariante: textoONulo(grupo),
      etiquetaVariante: conGrupo ? textoONulo(etiqueta) : null,
      ordenVariante: conGrupo ? (enteroDe(orden) ?? 0) : 0,
      stockInicial: enteroDe(stockInicial) ?? 0,
    });
  }

  const mensaje = aviso ?? falla;

  return (
    <form className="formulario-admin" onSubmit={enviar} noValidate>
      {mensaje !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{mensaje}</span>
        </p>
      ) : null}

      <div className="campos-dos">
        <div className="campo">
          <label htmlFor={`${id}-nombre`}>Nombre</label>
          <input
            id={`${id}-nombre`}
            value={nombre}
            onChange={(evento) => setNombre(evento.target.value)}
            required
            maxLength={200}
          />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-sku`}>Código</label>
          <input
            id={`${id}-sku`}
            className="mono"
            value={sku}
            onChange={(evento) => setSku(evento.target.value)}
            required
            maxLength={60}
          />
        </div>
      </div>

      <div className="campos-dos">
        <div className="campo">
          <label htmlFor={`${id}-categoria`}>Categoría</label>
          <select
            id={`${id}-categoria`}
            value={categoriaId}
            onChange={(evento) => setCategoriaId(Number(evento.target.value))}
          >
            {categorias.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.lineaNombre} · {categoria.nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-marca`}>Marca</label>
          <input
            id={`${id}-marca`}
            value={marca}
            onChange={(evento) => setMarca(evento.target.value)}
            maxLength={120}
          />
        </div>
      </div>

      <div className="campos-tres">
        <div className="campo">
          <label htmlFor={`${id}-precio`}>Precio (con IVA)</label>
          <EntradaMonto id={`${id}-precio`} valor={precio} alCambiar={setPrecio} required />
          <p className="ayuda-campo">En cero no se publica.</p>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-anterior`}>Precio anterior</label>
          <EntradaMonto id={`${id}-anterior`} valor={precioAnterior} alCambiar={setPrecioAnterior} />
          <p className="ayuda-campo">Solo si está en oferta.</p>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-unidad`}>Se vende por</label>
          <select
            id={`${id}-unidad`}
            value={unidadMedida}
            onChange={(evento) => setUnidadMedida(evento.target.value as UnidadMedida)}
          >
            <option value="unidad">Unidad</option>
            <option value="metro">Metro</option>
            <option value="kilo">Kilo</option>
          </select>
        </div>
      </div>

      <div className="campos-tres">
        <div className="campo">
          <label htmlFor={`${id}-grupo`}>Grupo de variantes</label>
          <input
            id={`${id}-grupo`}
            list={`${id}-grupos`}
            value={grupo}
            onChange={(evento) => setGrupo(evento.target.value)}
            maxLength={120}
          />
          <datalist id={`${id}-grupos`}>
            {gruposExistentes.map((existente) => (
              <option key={existente} value={existente} />
            ))}
          </datalist>
          <p className="ayuda-campo">
            Igual en todas las tallas o colores del mismo producto. Vacío si no tiene.
          </p>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-etiqueta`}>Opción</label>
          <input
            id={`${id}-etiqueta`}
            value={etiqueta}
            onChange={(evento) => setEtiqueta(evento.target.value)}
            disabled={!conGrupo}
            maxLength={120}
          />
          <p className="ayuda-campo">Lo que dice el botón: «Talla M», «Rojo».</p>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-orden`}>Orden</label>
          <input
            id={`${id}-orden`}
            inputMode="numeric"
            value={orden}
            onChange={(evento) => setOrden(evento.target.value)}
            disabled={!conGrupo}
          />
          <p className="ayuda-campo">0 va primero.</p>
        </div>
      </div>

      {conStockInicial ? (
        <div className="campo">
          <label htmlFor={`${id}-stock`}>Stock inicial</label>
          <input
            id={`${id}-stock`}
            inputMode="numeric"
            value={stockInicial}
            onChange={(evento) => setStockInicial(evento.target.value)}
          />
          <p className="ayuda-campo">
            Queda anotado en el inventario. Después el stock se cambia desde ahí.
          </p>
        </div>
      ) : null}

      <div className="admin-acciones">
        <button type="submit" className="btn btn-primario" disabled={enviando}>
          {enviando ? 'Guardando…' : textoBoton}
        </button>
        {guardado ? (
          <span className="etiqueta-ok" role="status">
            <Icono nombre="check" />
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}
